import { memo } from 'react';
import type { OrderWithItems } from '@/store/seller';

interface OrderReadyConfirmModalProps {
  isOpen: boolean;
  order: OrderWithItems | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const OrderReadyConfirmModal = memo(function OrderReadyConfirmModal({
  isOpen,
  order,
  onConfirm,
  onCancel
}: OrderReadyConfirmModalProps) {
  if (!isOpen || !order) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold text-gray-800 mb-4">준비완료 확인</h2>
        
        <div className="space-y-3 mb-6">
          {/* 주문 정보 */}
          <div className="border-b pb-3">
            <div className="flex justify-between">
              <span className="text-gray-600">주문번호</span>
              <span className="font-medium">{order.order_number}</span>
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-gray-600">주문자</span>
              <span className="font-medium">{order.buyer_nickname}</span>
            </div>
          </div>
          
          {/* 주문 상품 목록 */}
          <div className="border-b pb-3">
            <h3 className="font-medium mb-2">주문 상품</h3>
            {order.items.map((item, index) => (
              <div key={index} className="flex justify-between text-sm mb-1">
                <span>
                  {item.products?.name || item.product_name} x{item.quantity}
                </span>
                <span>
                  {formatCurrency((item.unit_price || item.price || 0) * item.quantity)}원
                </span>
              </div>
            ))}
          </div>
          
          {/* 총 금액 */}
          <div className="pt-2">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold">총 금액</span>
              <span className="text-xl font-bold text-green-600">
                {formatCurrency(order.total_amount)}원
              </span>
            </div>
          </div>
        </div>
        
        {/* 안내 메시지 */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
          <p className="text-sm text-yellow-800">
            준비완료 처리하시면 고객에게 알림이 전송됩니다.
            정말 준비가 완료되었습니까?
          </p>
        </div>
        
        {/* 액션 버튼 */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3 px-4 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
          >
            준비완료
          </button>
        </div>
      </div>
    </div>
  );
});